/**
 * React Query hooks for vendor operations.
 *
 * The list endpoint now returns joined org+profile rows. `useVendors` exposes
 * that shape directly. `useVendorProfiles` is a thin adapter for callers that
 * only need the flat procurement-profile array (PO/claim pickers, etc.).
 */
import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vendorApi } from '../services/procurementApi';
import { purchaseOrderKeys } from './usePurchaseOrders';
import type {
  VendorCreate,
  VendorProfile,
  VendorOrganizationSummary,
  VendorUpdate,
  ListVendorsParams,
  ListVendorOrdersParams,
  BulkCreateProfilesRequest,
} from '../types';

export const vendorKeys = {
  all: ['procurement-vendors'] as const,
  list: (params?: ListVendorsParams) => [...vendorKeys.all, 'list', params] as const,
  detail: (id: string) => [...vendorKeys.all, 'detail', id] as const,
};

function cleanParams(params?: ListVendorsParams): ListVendorsParams | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as ListVendorsParams;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

export function useVendors(
  params?: ListVendorsParams,
  options?: { enabled?: boolean },
) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableParams = useMemo(() => cleanParams(params), [
    params?.search,
    params?.is_active,
    params?.profile_filter,
    params?.sort_by,
    params?.sort_direction,
    params?.limit,
    params?.offset,
  ]);

  return useQuery({
    queryKey: vendorKeys.list(stableParams),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => vendorApi.list(stableParams),
    // Opt-out for a surface whose vendor picker is CONDITIONAL — the RFQ lines
    // table only routes a draft, so a sent RFQ must not pay for the whole
    // vendor list on every open.
    enabled: options?.enabled ?? true,
  });
}

/**
 * Legacy-compat adapter: returns only vendor profiles (flat array), filtered
 * to rows that actually have a procurement profile. Use this for pickers and
 * dropdowns that need a `vendor_id` (PO creation, supplier claim, drop-ship).
 */
export function useVendorProfiles(params?: Omit<ListVendorsParams, 'profile_filter'>) {
  const query = useVendors({ ...params, profile_filter: 'with_profile' });
  const data = useMemo<VendorProfile[]>(() => {
    if (!query.data) return [];
    return query.data.data
      .map((row) => row.profile)
      .filter((p): p is VendorProfile => p !== null);
  }, [query.data]);
  return { ...query, data };
}

/** A vendor selectable as a PO counterparty (see `useSelectableVendors`). */
export interface SelectableVendor {
  /** organization_id — the selectable value (PO.vendor_id references this). */
  organization_id: string;
  /** procurement vendor-profile id, or null for a profile-less vendor org. */
  vendor_profile_id: string | null;
  company_name: string;
  /** Vendor code, or null when the org has no procurement profile yet. */
  vendor_code: string | null;
  /** Profile currency, or null when the org has no procurement profile yet. */
  currency_code: string | null;
  /**
   * The vendor ORG's intercompany identity link — set when this vendor is one of
   * the tenant's own legal entities (docs/erp/intercompany-design.md).
   */
  linked_company_id: string | null;
}

/**
 * Vendors selectable as a purchase-order counterparty — every vendor-tagged
 * organization, whether or not it has a `proc_vendor_profiles` row. A PO's
 * `vendor_id` references the *organization* and `create_order` accepts a
 * profile-less vendor org, so import-created vendors (PI→PO wizard) and
 * intercompany counterparty orgs MUST be pickable; `useVendorProfiles`
 * (`with_profile`) wrongly hid them. Vendors whose profile is explicitly
 * deactivated (`is_active === false`) are excluded; a profile-less org is always
 * selectable (no profile ⇒ never deactivated). Single-sources the picker policy
 * for PO quick-create, drop-ship, special-order and requisition-convert.
 *
 * Do NOT pass `is_active` — that field lives on the profile and the backend
 * narrows to `with_profile` when it is set, re-hiding the profile-less orgs this
 * hook exists to surface; the deactivated-profile filter is applied client-side.
 */
export function useSelectableVendors(
  params?: Omit<ListVendorsParams, 'profile_filter' | 'is_active'>,
) {
  const query = useVendors({ ...params, profile_filter: 'all' });
  const data = useMemo<SelectableVendor[]>(() => {
    if (!query.data) return [];
    return query.data.data
      .filter((row) => row.profile === null || row.profile.is_active)
      .map((row) => ({
        organization_id: row.organization_id,
        vendor_profile_id: row.vendor_profile_id,
        company_name: row.profile?.company_name || row.organization.name,
        vendor_code: row.profile?.vendor_code ?? null,
        currency_code: row.profile?.currency_code ?? null,
        linked_company_id: row.organization.linked_company_id,
      }));
  }, [query.data]);
  return { ...query, data };
}

/**
 * All vendor-tagged organizations (with OR without a procurement profile), as
 * flat org summaries. Use for pickers that assign a supplier by *organization*
 * — RFQ sourcing — rather than by procurement-profile id: a CRM org tagged
 * `vendor` must be selectable to source an RFQ even before it has a
 * `proc_vendor_profiles` row (`set_suppliers` only requires a tenant-owned org).
 * Unlike `useVendorProfiles`, do NOT pass `is_active` — that field lives on the
 * profile and the backend narrows to `with_profile` when it is set, which would
 * re-hide the profile-less orgs this hook exists to surface.
 */
export function useVendorOrganizations(
  params?: Omit<ListVendorsParams, 'profile_filter' | 'is_active'>,
  options?: { enabled?: boolean },
) {
  const query = useVendors({ ...params, profile_filter: 'all' }, options);
  const data = useMemo<VendorOrganizationSummary[]>(() => {
    if (!query.data) return [];
    return query.data.data.map((row) => row.organization);
  }, [query.data]);
  return { ...query, data };
}

export function useVendor(id: string) {
  return useQuery({
    queryKey: vendorKeys.detail(id),
    queryFn: () => vendorApi.get(id),
    enabled: !!id,
  });
}

/**
 * A page of a vendor's purchase orders, keyed by the vendor *profile* id (the
 * route param). Server-side paginated — the history is unbounded, so the caller
 * always asks for one page and reads `pagination.total` for the page controls.
 */
export function useVendorOrders(id: string, params?: ListVendorOrdersParams) {
  return useQuery({
    queryKey: purchaseOrderKeys.byVendor(id, params),
    queryFn: () => vendorApi.listOrders(id, params),
    enabled: !!id,
    // Keep the previous page's rows visible while the next page loads (no
    // flicker) — but ONLY within the same vendor. `byVendor` places the vendor
    // id at key index 2; when it differs, drop the placeholder so one vendor's
    // POs never render under another vendor's header on route reuse.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === id ? previousData : undefined,
  });
}

export function useCreateVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: VendorCreate) => vendorApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorKeys.all });
    },
  });
}

export function useBulkCreateVendorProfiles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkCreateProfilesRequest) => vendorApi.bulkCreateProfiles(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorKeys.all });
    },
  });
}

export function useUpdateVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: VendorUpdate }) => vendorApi.update(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: vendorKeys.all });
      queryClient.invalidateQueries({ queryKey: vendorKeys.detail(id) });
    },
  });
}

export function useDeleteVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => vendorApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorKeys.all });
    },
  });
}
